// テキスト図形の「文字範囲だけへ適用する部分書式（inline style run）」の共有ロジック。
//
// 方針:
// - run の offset は保存本文（object.text）先頭からの UTF-16 code unit 数で、`start` を含み
//   `end` を含まない半開区間。Selection API・JavaScript の文字列と同じ座標系を正本とする。
// - 保存形は「start 昇順・非重複・基底書式との差分だけ・隣接同値は結合済み」の正規形に固定する。
//   サーバ（op-applier / REST・MCP）とクライアント（楽観適用・編集・描画）が同じ純粋関数を
//   共有し、経路差で run がずれないようにする。
// - 適用・クリア・rebase・集計はいずれも「文字ごとの override 表」を作ってから正規形へ畳み直す
//   実装にそろえる。区間演算を個別に書くより分岐が少なく、分割・結合・境界の取りこぼしを防げる。
// - 本モジュールは純粋関数のみで、DOM・React・apps/web へ依存しない。

import {
  buildMarkdownSourceMap,
  clipRangeToMarkdownVisible,
  markdownLineDisplaySegments,
  markdownRangeDisplaySegments,
  markdownVisibleRanges,
  type MarkdownDisplaySegment,
  type MarkdownInlineDecoration,
  type MarkdownSourceLine,
  type MarkdownSourceMap,
  type RawOffsetRange,
} from "./markdown-source-map";
import { TEXT_BODY_MAX_LENGTH } from "./text-body-limit";

/** 部分書式として上書きできる項目（文字寄せは行・図形全体の概念のため含めない）。 */
export const TEXT_INLINE_STYLE_KEYS = ["color", "fill", "fontSize", "bold"] as const;

export type TextInlineStyleKey = (typeof TEXT_INLINE_STYLE_KEYS)[number];

/** 部分書式の 1 区間ぶんの上書き内容（基底書式との差分だけを持つ）。 */
export interface TextInlineStyle {
  readonly color?: string | undefined;
  readonly fill?: string | undefined;
  readonly fontSize?: number | undefined;
  readonly bold?: boolean | undefined;
}

/** 部分書式の 1 区間（`[start, end)`）。 */
export interface TextInlineStyleRange {
  readonly start: number;
  readonly end: number;
  readonly style: TextInlineStyle;
}

/**
 * 範囲書式パッチ 1 件。`null` はその項目の override を削除する（基底書式へ戻す）ことを表す。
 * `undefined`（キー自体が無い）は「その項目に触らない」。
 */
export interface TextInlineStylePatch {
  readonly color?: string | null | undefined;
  readonly fill?: string | null | undefined;
  readonly fontSize?: number | null | undefined;
  readonly bold?: boolean | null | undefined;
}

/** 範囲書式パッチ（適用区間つき）。 */
export interface TextInlineRangePatch {
  readonly start: number;
  readonly end: number;
  readonly style: TextInlineStylePatch;
}

/** 全文（基底書式）の部分更新。指定した項目だけを基底へ反映する。 */
export type TextBaseStylePatch = TextInlineStyle;

/** 1 つのテキスト図形が保持できる run の上限。 */
export const TEXT_INLINE_STYLE_RUNS_MAX = 256;

/**
 * 1 つの `object.text.style.update` で受け付ける range patch の上限。
 * 通常操作（1 件）に加え、Undo 復元（対象範囲のクリア 1 件 + 変更前 override の復元 256 件）を
 * 1 op で収められるよう `TEXT_INLINE_STYLE_RUNS_MAX + 1` とする。
 */
export const TEXT_INLINE_STYLE_RANGE_PATCHES_MAX = TEXT_INLINE_STYLE_RUNS_MAX + 1;

/**
 * run の fontSize として受け付ける範囲（px）。配置前・選択後パネルの選択肢と同じ。
 * 下限は、拡大率 400% で現行プリセット「小」（16px）と同じ見え方になる 4px とする。
 * これより小さいと等倍で字形が潰れ、図形がそこにあること自体に気づけなくなる。
 */
export const TEXT_INLINE_FONT_SIZE_MIN_PX = 4;
export const TEXT_INLINE_FONT_SIZE_MAX_PX = 128;

/**
 * 全文（基底）の fontSize として受け付ける範囲（px）。
 * 既存 `object.style.update` の受理範囲と同じにそろえ、旧データの範囲外 fontSize を
 * Undo で復元する op が受理されなくなる退行を避ける。
 */
export const TEXT_BASE_FONT_SIZE_MIN_PX = 1;
export const TEXT_BASE_FONT_SIZE_MAX_PX = 512;

/** run の色（color / fill）として受け付ける形式（6 桁 hex のみ）。 */
export const TEXT_INLINE_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;

/**
 * `expectedText` として受け付ける最大長（本文の保存上限と同じ）。
 * 本文長上限から導出し、2 か所が将来ずれないようにする。
 */
export const TEXT_STYLE_EXPECTED_TEXT_MAX_LENGTH = TEXT_BODY_MAX_LENGTH;

/**
 * 受理する `textStyleRevision` の上限。
 * これを超える値を保存できると、`+ 1` しても値が変わらない領域（2^53 以上）へ到達し、
 * 「保存直前の revision 一致」で競合を排他する仕組みが機能しなくなる。
 * サーバは作成時に 0 から始め、op の確定ごとに 1 ずつ進めるため、実運用でこの上限へ届かない。
 */
export const TEXT_STYLE_REVISION_MAX = Number.MAX_SAFE_INTEGER - 1;

/** 本文・部分書式・全文書式の競合境界に使う revision（未設定は 0 として扱う）。 */
export function textStyleRevisionOf(value: number | undefined): number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? Math.floor(value) : 0;
}

/**
 * 次に採番する `textStyleRevision`。上限へ達していて安全に進められない場合は null を返す。
 *
 * 上限を超えた値を保存すると、その図形は object schema と `expectedTextStyleRevision` の
 * 受理範囲から外れ、以後の部分書式付き更新が入力検証で拒否される。さらに 2^53 以降は
 * `+ 1` しても値が変わらず、「保存直前の revision 一致」による排他も失われる。
 * 採番の可否をこの 1 か所へ集約し、revision を進めるすべての経路が同じ判定で更新を
 * 拒否できるようにする（無条件に `+ 1` する経路を残さない）。
 */
export function nextTextStyleRevision(value: number | undefined): number | null {
  const current = textStyleRevisionOf(value);
  return current >= TEXT_STYLE_REVISION_MAX ? null : current + 1;
}

//------------------------------------------------------------------------------
// 検証
//------------------------------------------------------------------------------

/** run の style に 1 項目以上の有効値があるか。 */
export function isNonEmptyTextInlineStyle(style: TextInlineStyle): boolean {
  return TEXT_INLINE_STYLE_KEYS.some((key) => style[key] !== undefined);
}

/**
 * 外部入力（API・旧クライアント）の run 配列が保存できる正規形かを検証する。
 * 形式・値域・順序・非重複・件数・本文との整合をすべて満たすときだけ true を返す。
 *
 * `text` にはその run が属する本文を渡す（境界がサロゲートペアの途中に落ちていないかを見るため。
 * 途中境界を保存すると、編集 DOM や表示の分割で高位サロゲートだけが切り出されて文字化けする）。
 */
export function isValidTextInlineStyleRanges(
  runs: readonly TextInlineStyleRange[],
  text: string
): boolean {
  if (runs.length > TEXT_INLINE_STYLE_RUNS_MAX) return false;
  let previousEnd = 0;
  let previousStyle: TextInlineStyle | null = null;
  for (const run of runs) {
    if (!Number.isInteger(run.start) || !Number.isInteger(run.end)) return false;
    if (run.start < 0 || run.end > text.length || run.start >= run.end) return false;
    if (run.start < previousEnd) return false;
    if (isInsideSurrogatePair(text, run.start) || isInsideSurrogatePair(text, run.end)) return false;
    // 隣接して style が同じ run は 1 件へ結合済みであることが保存形の不変条件（分けて保存すると
    // 件数上限を無駄に消費し、正規形との往復で配列が変わる）。
    if (run.start === previousEnd && previousStyle !== null) {
      if (isSameTextInlineStyle(run.style, previousStyle)) return false;
    }
    if (!isNonEmptyTextInlineStyle(run.style)) return false;
    if (run.style.color !== undefined && !TEXT_INLINE_COLOR_PATTERN.test(run.style.color)) {
      return false;
    }
    if (run.style.fill !== undefined && !TEXT_INLINE_COLOR_PATTERN.test(run.style.fill)) {
      return false;
    }
    if (run.style.fontSize !== undefined && !isValidTextInlineFontSize(run.style.fontSize)) {
      return false;
    }
    if (run.style.bold !== undefined && typeof run.style.bold !== "boolean") return false;
    previousEnd = run.end;
    previousStyle = run.style;
  }
  return true;
}

/** `index` がサロゲートペアの途中（上位と下位の間）か。 */
function isInsideSurrogatePair(text: string, index: number): boolean {
  if (index <= 0 || index >= text.length) return false;
  const high = text.charCodeAt(index - 1);
  const low = text.charCodeAt(index);
  return high >= 0xd800 && high <= 0xdbff && low >= 0xdc00 && low <= 0xdfff;
}

/** run の fontSize が受理範囲の整数か。 */
export function isValidTextInlineFontSize(value: number): boolean {
  return (
    Number.isInteger(value) &&
    value >= TEXT_INLINE_FONT_SIZE_MIN_PX &&
    value <= TEXT_INLINE_FONT_SIZE_MAX_PX
  );
}

/** 全文（基底）の fontSize が受理範囲の整数か。 */
export function isValidTextBaseFontSize(value: number): boolean {
  return (
    Number.isInteger(value) &&
    value >= TEXT_BASE_FONT_SIZE_MIN_PX &&
    value <= TEXT_BASE_FONT_SIZE_MAX_PX
  );
}

//------------------------------------------------------------------------------
// 文字ごとの override 表（内部表現）
//------------------------------------------------------------------------------

/**
 * 文字ごとの override 表。キーごとに本文長ぶんの配列を持ち、`undefined` は
 * 「その文字はその項目を上書きしていない（基底書式のまま）」を表す。
 */
type OverrideMap = {
  color: (string | undefined)[];
  fill: (string | undefined)[];
  fontSize: (number | undefined)[];
  bold: (boolean | undefined)[];
};

function createOverrideMap(length: number): OverrideMap {
  return {
    bold: new Array<boolean | undefined>(length).fill(undefined),
    color: new Array<string | undefined>(length).fill(undefined),
    fill: new Array<string | undefined>(length).fill(undefined),
    fontSize: new Array<number | undefined>(length).fill(undefined),
  };
}

function writeRunsIntoMap(
  map: OverrideMap,
  runs: readonly TextInlineStyleRange[],
  length: number
): void {
  for (const run of runs) {
    const start = Math.max(0, Math.floor(run.start));
    const end = Math.min(length, Math.floor(run.end));
    for (let i = start; i < end; i += 1) {
      if (run.style.color !== undefined) map.color[i] = run.style.color;
      if (run.style.fill !== undefined) map.fill[i] = run.style.fill;
      if (run.style.fontSize !== undefined) map.fontSize[i] = run.style.fontSize;
      if (run.style.bold !== undefined) map.bold[i] = run.style.bold;
    }
  }
}

/** 文字 `index` の override だけを取り出す（基底書式は含めない）。 */
function styleAtIndex(map: OverrideMap, index: number): TextInlineStyle {
  const style: {
    color?: string | undefined;
    fill?: string | undefined;
    fontSize?: number | undefined;
    bold?: boolean | undefined;
  } = {};
  const color = map.color[index];
  if (color !== undefined) style.color = color;
  const fill = map.fill[index];
  if (fill !== undefined) style.fill = fill;
  const fontSize = map.fontSize[index];
  if (fontSize !== undefined) style.fontSize = fontSize;
  const bold = map.bold[index];
  if (bold !== undefined) style.bold = bold;
  return style;
}

/**
 * 可視本文の外（記法プレフィックス・改行）と、基底書式と同値の override を落とす。
 * `bold: false` は基底 bold が true のときだけ差分として残る（generic な同値判定の帰結）。
 */
function pruneOverrideMap(
  map: OverrideMap,
  base: TextInlineStyle,
  length: number,
  visible: readonly boolean[]
): void {
  for (let i = 0; i < length; i += 1) {
    if (!visible[i]) {
      map.color[i] = undefined;
      map.fill[i] = undefined;
      map.fontSize[i] = undefined;
      map.bold[i] = undefined;
      continue;
    }
    if (map.color[i] !== undefined && isSameColorValue(map.color[i], base.color)) {
      map.color[i] = undefined;
    }
    if (map.fill[i] !== undefined && isSameFillValue(map.fill[i], base.fill)) {
      map.fill[i] = undefined;
    }
    if (map.fontSize[i] !== undefined && map.fontSize[i] === base.fontSize) {
      map.fontSize[i] = undefined;
    }
    if (map.bold[i] !== undefined && (map.bold[i] ?? false) === (base.bold ?? false)) {
      map.bold[i] = undefined;
    }
  }
}

/**
 * サロゲートペアの途中に落ちた境界を外向きへ丸める。
 * プログラム生成の境界（範囲パッチ・rebase）で文字化けを起こさないための決定論的な補正で、
 * 結合文字・ZWJ 列にはブラウザ Selection が返した境界をそのまま尊重する（補正しない）。
 */
function roundOverrideMapToCodePoints(map: OverrideMap, text: string): void {
  for (let i = 1; i < text.length; i += 1) {
    const high = text.charCodeAt(i - 1);
    const low = text.charCodeAt(i);
    const isPair = high >= 0xd800 && high <= 0xdbff && low >= 0xdc00 && low <= 0xdfff;
    if (!isPair) continue;
    unifyPairValue(map.color, i);
    unifyPairValue(map.fill, i);
    unifyPairValue(map.fontSize, i);
    unifyPairValue(map.bold, i);
  }
}

/** 指定した項目の override 表を丸ごと消す（キーごとに配列の型が違うため switch で解決する）。 */
function clearOverrideKey(map: OverrideMap, key: TextInlineStyleKey): void {
  switch (key) {
    case "color":
      map.color.fill(undefined);
      return;
    case "fill":
      map.fill.fill(undefined);
      return;
    case "fontSize":
      map.fontSize.fill(undefined);
      return;
    case "bold":
      map.bold.fill(undefined);
      return;
  }
}

function unifyPairValue<T>(values: (T | undefined)[], lowIndex: number): void {
  const head = values[lowIndex - 1];
  const tail = values[lowIndex];
  if (head === tail) return;
  // 外向きに丸める＝どちらか一方でも override があればペア全体へ広げる。
  // 値が食い違う場合はペア先頭側（文字の始まり）を採る。
  const unified = head !== undefined ? head : tail;
  values[lowIndex - 1] = unified;
  values[lowIndex] = unified;
}

/** override 表を「start 昇順・非重複・隣接同値は結合済み」の run 配列へ畳み直す。 */
function collectRunsFromMap(map: OverrideMap, length: number): readonly TextInlineStyleRange[] {
  const runs: TextInlineStyleRange[] = [];
  let runStart = -1;
  let runStyle: TextInlineStyle = {};
  for (let i = 0; i < length; i += 1) {
    const style = styleAtIndex(map, i);
    const hasStyle = isNonEmptyTextInlineStyle(style);
    if (runStart >= 0 && (!hasStyle || !isSameTextInlineStyle(style, runStyle))) {
      runs.push({ end: i, start: runStart, style: runStyle });
      runStart = -1;
    }
    if (hasStyle && runStart < 0) {
      runStart = i;
      runStyle = style;
    }
  }
  if (runStart >= 0) {
    runs.push({ end: length, start: runStart, style: runStyle });
  }
  return runs;
}

function buildVisibleFlags(map: MarkdownSourceMap, length: number): boolean[] {
  const visible = new Array<boolean>(length).fill(false);
  for (const range of markdownVisibleRanges(map)) {
    for (let i = range.start; i < range.end; i += 1) visible[i] = true;
  }
  return visible;
}

//------------------------------------------------------------------------------
// 同値判定
//------------------------------------------------------------------------------

const HEX_COLOR_PATTERN = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

/** hex 色を比較用へ正規化する（短縮形の展開と小文字化。hex 以外はそのまま）。 */
function normalizeColorForCompare(value: string): string {
  if (!HEX_COLOR_PATTERN.test(value)) return value;
  const lowered = value.trim().toLowerCase();
  if (lowered.length === 4) {
    const r = lowered[1] ?? "0";
    const g = lowered[2] ?? "0";
    const b = lowered[3] ?? "0";
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return lowered;
}

function isSameColorValue(a: string | undefined, b: string | undefined): boolean {
  if (a === undefined || b === undefined) return a === b;
  return normalizeColorForCompare(a) === normalizeColorForCompare(b);
}

/** 背景色は「hex 以外・未設定はすべて背景なし」として同一視する（描画側の解決と同じ規則）。 */
function isSameFillValue(a: string | undefined, b: string | undefined): boolean {
  const normalize = (value: string | undefined): string =>
    value !== undefined && HEX_COLOR_PATTERN.test(value)
      ? normalizeColorForCompare(value)
      : "transparent";
  return normalize(a) === normalize(b);
}

/** 2 つの override が同じ内容か（隣接 run の結合判定に使う）。 */
export function isSameTextInlineStyle(a: TextInlineStyle, b: TextInlineStyle): boolean {
  return (
    isSameColorValue(a.color, b.color) &&
    isSameFillValue(a.fill, b.fill) &&
    a.fontSize === b.fontSize &&
    // bold は「未設定（override なし）」と「false（明示的に通常ウェイト）」を区別する
    // （基底 bold が true のとき false は有効な override になる）。
    a.bold === b.bold
  );
}

//------------------------------------------------------------------------------
// 正規化・適用・クリア
//------------------------------------------------------------------------------

export interface NormalizeTextInlineStylesParams {
  readonly runs: readonly TextInlineStyleRange[] | undefined;
  readonly text: string;
  readonly base: TextInlineStyle;
  /** 事前に構築した source map（省略時は text から構築する）。 */
  readonly sourceMap?: MarkdownSourceMap | undefined;
}

/**
 * run 配列を正規形（start 昇順・非重複・基底との差分のみ・隣接同値は結合）へ揃える。
 * 可視本文の外（記法プレフィックス・改行）と空の style は落とし、サロゲートペアの
 * 途中境界は外向きへ丸める。
 */
export function normalizeTextInlineStyles(
  params: NormalizeTextInlineStylesParams
): readonly TextInlineStyleRange[] {
  const { base, text } = params;
  const length = text.length;
  if (length === 0) return [];
  const sourceMap = params.sourceMap ?? buildMarkdownSourceMap(text);
  const map = createOverrideMap(length);
  writeRunsIntoMap(map, params.runs ?? [], length);
  pruneOverrideMap(map, base, length, buildVisibleFlags(sourceMap, length));
  roundOverrideMapToCodePoints(map, text);
  return collectRunsFromMap(map, length);
}

export interface ApplyTextInlineRangePatchesParams {
  readonly runs: readonly TextInlineStyleRange[] | undefined;
  readonly text: string;
  readonly base: TextInlineStyle;
  readonly patches: readonly TextInlineRangePatch[];
  readonly sourceMap?: MarkdownSourceMap | undefined;
}

/**
 * 範囲書式パッチを順に適用し、正規形の run 配列を返す。
 * 各パッチは可視本文へ切り出してから適用するため、記法プレフィックスや改行だけの区間には
 * run を作らない。`null` の項目は override を削除する。
 */
export function applyTextInlineRangePatches(
  params: ApplyTextInlineRangePatchesParams
): readonly TextInlineStyleRange[] {
  const { base, patches, text } = params;
  const length = text.length;
  if (length === 0) return [];
  const sourceMap = params.sourceMap ?? buildMarkdownSourceMap(text);
  const map = createOverrideMap(length);
  writeRunsIntoMap(map, params.runs ?? [], length);
  for (const patch of patches) {
    for (const range of clipRangeToMarkdownVisible(sourceMap, patch.start, patch.end)) {
      applyPatchToMapRange(map, patch.style, range);
    }
  }
  pruneOverrideMap(map, base, length, buildVisibleFlags(sourceMap, length));
  roundOverrideMapToCodePoints(map, text);
  return collectRunsFromMap(map, length);
}

function applyPatchToMapRange(
  map: OverrideMap,
  patch: TextInlineStylePatch,
  range: RawOffsetRange
): void {
  for (let i = range.start; i < range.end; i += 1) {
    if (patch.color !== undefined) map.color[i] = patch.color === null ? undefined : patch.color;
    if (patch.fill !== undefined) map.fill[i] = patch.fill === null ? undefined : patch.fill;
    if (patch.fontSize !== undefined) {
      map.fontSize[i] = patch.fontSize === null ? undefined : patch.fontSize;
    }
    if (patch.bold !== undefined) map.bold[i] = patch.bold === null ? undefined : patch.bold;
  }
}

/**
 * 指定した項目の override をすべて削除する（全文書式の明示変更に伴う消去）。
 * 他の項目の override は保持する。
 */
export function clearTextInlineStyleKeys(params: {
  readonly runs: readonly TextInlineStyleRange[] | undefined;
  readonly text: string;
  readonly base: TextInlineStyle;
  readonly keys: readonly TextInlineStyleKey[];
  readonly sourceMap?: MarkdownSourceMap | undefined;
}): readonly TextInlineStyleRange[] {
  const { base, keys, text } = params;
  const length = text.length;
  if (length === 0) return [];
  const sourceMap = params.sourceMap ?? buildMarkdownSourceMap(text);
  const map = createOverrideMap(length);
  writeRunsIntoMap(map, params.runs ?? [], length);
  for (const key of keys) {
    clearOverrideKey(map, key);
  }
  pruneOverrideMap(map, base, length, buildVisibleFlags(sourceMap, length));
  roundOverrideMapToCodePoints(map, text);
  return collectRunsFromMap(map, length);
}

//------------------------------------------------------------------------------
// 本文変更に伴う rebase
//------------------------------------------------------------------------------

/** 前本文と次本文から求めた単一置換差分。 */
export interface SingleTextReplacement {
  /** 置換された旧本文の開始 offset。 */
  readonly start: number;
  /** 置換された旧本文の終端 offset（半開区間）。 */
  readonly end: number;
  /** 挿入された文字列。 */
  readonly inserted: string;
}

/**
 * 前本文と次本文の最長共通 prefix / suffix から、1 回の置換差分を求める。
 * サーバ（旧クライアント由来の本文更新）と web（編集中の追従）が同じ結果へ収束するための正本。
 */
export function computeSingleTextReplacement(
  prevText: string,
  nextText: string
): SingleTextReplacement {
  const maxPrefix = Math.min(prevText.length, nextText.length);
  let prefix = 0;
  while (prefix < maxPrefix && prevText[prefix] === nextText[prefix]) prefix += 1;
  let suffix = 0;
  while (
    suffix < maxPrefix - prefix &&
    prevText[prevText.length - 1 - suffix] === nextText[nextText.length - 1 - suffix]
  ) {
    suffix += 1;
  }
  return {
    end: prevText.length - suffix,
    inserted: nextText.slice(prefix, nextText.length - suffix),
    start: prefix,
  };
}

/**
 * 本文が変わったときに run を移動・分割・削除して追従させる。
 * 挿入文字は「直前文字 → 行頭では直後文字 → 基底書式」の順で有効な override を継承する。
 */
export function rebaseTextInlineStyles(params: {
  readonly runs: readonly TextInlineStyleRange[] | undefined;
  readonly prevText: string;
  readonly nextText: string;
  readonly base: TextInlineStyle;
}): readonly TextInlineStyleRange[] {
  const { base, nextText, prevText } = params;
  const runs = params.runs ?? [];
  if (nextText.length === 0) return [];
  if (runs.length === 0) return [];
  if (prevText === nextText) {
    return normalizeTextInlineStyles({ base, runs, text: nextText });
  }
  const diff = computeSingleTextReplacement(prevText, nextText);
  const previous = createOverrideMap(prevText.length);
  writeRunsIntoMap(previous, runs, prevText.length);
  const next = createOverrideMap(nextText.length);
  // 置換より前は 1:1 で維持する。
  for (let i = 0; i < diff.start; i += 1) copyOverride(previous, i, next, i);
  // 挿入文字は境界の有効 override を継承する（直前文字優先。行頭では直後文字）。
  const inheritIndex = resolveInheritedOverrideIndex(prevText, diff);
  for (let i = 0; i < diff.inserted.length; i += 1) {
    if (inheritIndex !== null) copyOverride(previous, inheritIndex, next, diff.start + i);
  }
  // 置換より後は、長さ差分だけ移動して維持する（削除区間内だけの run は捨てられる）。
  const shift = diff.start + diff.inserted.length - diff.end;
  for (let i = diff.end; i < prevText.length; i += 1) copyOverride(previous, i, next, i + shift);
  pruneOverrideMap(
    next,
    base,
    nextText.length,
    buildVisibleFlags(buildMarkdownSourceMap(nextText), nextText.length)
  );
  roundOverrideMapToCodePoints(next, nextText);
  return collectRunsFromMap(next, nextText.length);
}

function copyOverride(
  from: OverrideMap,
  fromIndex: number,
  to: OverrideMap,
  toIndex: number
): void {
  if (toIndex < 0 || toIndex >= to.color.length) return;
  to.color[toIndex] = from.color[fromIndex];
  to.fill[toIndex] = from.fill[fromIndex];
  to.fontSize[toIndex] = from.fontSize[fromIndex];
  to.bold[toIndex] = from.bold[fromIndex];
}

/**
 * 挿入文字が継承する override の参照元（前本文上の文字 index）を返す。
 * 直前文字が同じ行にあればそれを、行頭では直後文字を、いずれも無ければ null（基底書式）を返す。
 */
function resolveInheritedOverrideIndex(
  prevText: string,
  diff: SingleTextReplacement
): number | null {
  if (diff.start > 0 && prevText[diff.start - 1] !== "\n" && prevText[diff.start - 1] !== "\r") {
    return diff.start - 1;
  }
  if (diff.end < prevText.length && prevText[diff.end] !== "\n" && prevText[diff.end] !== "\r") {
    return diff.end;
  }
  return null;
}

//------------------------------------------------------------------------------
// 集計（範囲パネルの現在値）
//------------------------------------------------------------------------------

/** 範囲内の有効値。`mixed` が true のときは 2 値以上が混在している。 */
export interface TextInlineStyleAggregate {
  readonly color: { readonly value: string | undefined; readonly mixed: boolean };
  readonly fill: { readonly value: string | undefined; readonly mixed: boolean };
  readonly fontSize: { readonly value: number | undefined; readonly mixed: boolean };
  readonly bold: { readonly value: boolean | undefined; readonly mixed: boolean };
}

/**
 * 選択範囲の可視文字について、`override ?? 基底` の有効値を集計する。
 * 可視文字が 1 つも無い場合は基底書式をそのまま返す（混在なし）。
 */
export function aggregateTextInlineStyle(params: {
  readonly runs: readonly TextInlineStyleRange[] | undefined;
  readonly text: string;
  readonly base: TextInlineStyle;
  readonly start: number;
  readonly end: number;
  readonly sourceMap?: MarkdownSourceMap | undefined;
}): TextInlineStyleAggregate {
  const { base, end, start, text } = params;
  const sourceMap = params.sourceMap ?? buildMarkdownSourceMap(text);
  const ranges = clipRangeToMarkdownVisible(sourceMap, start, end);
  const map = createOverrideMap(text.length);
  writeRunsIntoMap(map, params.runs ?? [], text.length);
  const colors: (string | undefined)[] = [];
  const fills: (string | undefined)[] = [];
  const fontSizes: (number | undefined)[] = [];
  const bolds: (boolean | undefined)[] = [];
  for (const range of ranges) {
    for (let i = range.start; i < range.end; i += 1) {
      colors.push(map.color[i] ?? base.color);
      fills.push(map.fill[i] ?? base.fill);
      fontSizes.push(map.fontSize[i] ?? base.fontSize);
      bolds.push(map.bold[i] ?? base.bold);
    }
  }
  if (colors.length === 0) {
    return {
      bold: { mixed: false, value: base.bold },
      color: { mixed: false, value: base.color },
      fill: { mixed: false, value: base.fill },
      fontSize: { mixed: false, value: base.fontSize },
    };
  }
  return {
    bold: aggregateValues(bolds, (a, b) => (a ?? false) === (b ?? false)),
    color: aggregateValues(colors, isSameColorValue),
    fill: aggregateValues(fills, isSameFillValue),
    fontSize: aggregateValues(fontSizes, (a, b) => a === b),
  };
}

function aggregateValues<T>(
  values: readonly (T | undefined)[],
  isSame: (a: T | undefined, b: T | undefined) => boolean
): { readonly value: T | undefined; readonly mixed: boolean } {
  const first = values[0];
  const mixed = values.some((value) => !isSame(value, first));
  return { mixed, value: mixed ? undefined : first };
}

//------------------------------------------------------------------------------
// 描画向けの分割
//------------------------------------------------------------------------------

/** 編集 DOM 向けの分割結果（raw 本文の部分文字列 + その区間の override）。 */
export interface TextInlineRawPiece {
  readonly text: string;
  readonly rawStart: number;
  readonly rawEnd: number;
  readonly style: TextInlineStyle;
}

/**
 * raw 区間 `[rawStart, rawEnd)` を run 境界で分割する（編集 DOM の span 分割に使う）。
 * 区間が空のときは、Range を張る先を保証するため空の 1 片を返す。
 */
export function splitRawRangeByRuns(params: {
  readonly text: string;
  readonly rawStart: number;
  readonly rawEnd: number;
  readonly runs: readonly TextInlineStyleRange[] | undefined;
}): readonly TextInlineRawPiece[] {
  const { rawEnd, rawStart, text } = params;
  if (rawEnd <= rawStart) {
    return [{ rawEnd: rawStart, rawStart, style: {}, text: "" }];
  }
  const map = createOverrideMap(text.length);
  writeRunsIntoMap(map, params.runs ?? [], text.length);
  const pieces: TextInlineRawPiece[] = [];
  let pieceStart = rawStart;
  let pieceStyle = styleAtIndex(map, rawStart);
  for (let i = rawStart + 1; i <= rawEnd; i += 1) {
    const style = i < rawEnd ? styleAtIndex(map, i) : null;
    if (style !== null && isSameTextInlineStyle(style, pieceStyle)) continue;
    pieces.push({
      rawEnd: i,
      rawStart: pieceStart,
      style: pieceStyle,
      text: text.slice(pieceStart, i),
    });
    if (style !== null) {
      pieceStart = i;
      pieceStyle = style;
    }
  }
  return pieces;
}

/** 上書きが 1 件も無い区間で共有する override（毎文字オブジェクトを作らないため）。 */
const EMPTY_TEXT_INLINE_STYLE: TextInlineStyle = {};

/** 非編集表示・PDF 向けの分割結果（表示テキスト + リンク + その区間の override + 記法の装飾）。 */
export interface TextInlineDisplaySpan {
  readonly text: string;
  readonly url?: string | undefined;
  readonly style: TextInlineStyle;
  /** 記法から導かれるインライン装飾（この span のすべての文字で同値）。 */
  readonly decoration: MarkdownInlineDecoration;
  /** 行内で 0 から振るコード範囲の識別子（コード範囲の外は undefined）。 */
  readonly codeRangeIndex?: number | undefined;
  /** 論理コード範囲の先頭 span か（左 padding と左角丸を当てる対象）。 */
  readonly codeStart?: boolean | undefined;
  /** 論理コード範囲の末尾 span か（右 padding と右角丸を当てる対象）。 */
  readonly codeEnd?: boolean | undefined;
}

/**
 * 1 行の表示テキストを、リンク境界・run 境界・記法の装飾境界の和集合で分割する。
 * `[label](URL)` は label へ縮約され、括弧・URL だけに付いた run は縮約表示では描画対象を持たない。
 *
 * `style`・`decoration`・`codeRangeIndex` の 3 つがすべて同値の間だけを 1 つの span にまとめる。
 * `codeRangeIndex` を判定から外すと、隣接する別のコードスパンが 1 つへ結合され、padding と
 * 背景矩形を範囲ごとに 1 回ずつ当てられなくなる。
 */
export function buildTextInlineDisplaySpans(params: {
  readonly line: MarkdownSourceLine;
  readonly text: string;
  readonly runs: readonly TextInlineStyleRange[] | undefined;
}): readonly TextInlineDisplaySpan[] {
  return buildDisplaySpansFromSegments(markdownLineDisplaySegments(params.line), params.runs);
}

/**
 * 指定した raw 範囲 `[start, end)`（表のセルなど）について、run との合成済み表示 span を返す。
 * 行版（`buildTextInlineDisplaySpans`）と同じ実装を通るため、セルの中でも部分書式・リンク・
 * 記法の装飾が行と同じ規則で働く。`collapsePipeEscape` は表のセル専用（`\|` → `|`）。
 */
export function buildTextInlineDisplaySpansForRange(params: {
  readonly text: string;
  readonly start: number;
  readonly end: number;
  readonly runs: readonly TextInlineStyleRange[] | undefined;
  readonly collapsePipeEscape?: boolean | undefined;
}): readonly TextInlineDisplaySpan[] {
  return buildDisplaySpansFromSegments(
    markdownRangeDisplaySegments(params.text, params.start, params.end, {
      collapsePipeEscape: params.collapsePipeEscape,
    }),
    params.runs
  );
}

/** 表示セグメント列を run 境界で分割して span 列にする共通実装（行版・範囲版が共有する）。 */
function buildDisplaySpansFromSegments(
  segments: readonly MarkdownDisplaySegment[],
  runs: readonly TextInlineStyleRange[] | undefined
): readonly TextInlineDisplaySpan[] {
  // run が 1 件も無い本文では override 表を作らない。本関数は行・表のセルごとに呼ばれるため、
  // 本文長ぶんの配列を毎回確保すると「行数（セル数）× 本文長」の確保になる（装飾だけの本文・
  // 記法を含まない本文でも全行を通るため、ここを素通りさせないと表示・計測・PDF が一斉に重くなる）。
  const window = createSegmentOverrideWindow(segments, runs);
  const spans: TextInlineDisplaySpan[] = [];
  for (const segment of segments) {
    let pieceStart = 0;
    let pieceStyle: TextInlineStyle | null = null;
    const push = (end: number): void => {
      if (pieceStyle === null) return;
      spans.push({
        decoration: segment.decoration,
        style: pieceStyle,
        text: segment.text.slice(pieceStart, end),
        ...(segment.url !== undefined ? { url: segment.url } : {}),
        ...(segment.codeRangeIndex !== undefined
          ? { codeRangeIndex: segment.codeRangeIndex }
          : {}),
      });
    };
    for (let i = 0; i < segment.text.length; i += 1) {
      const rawOffset = segment.rawOffsets[i];
      const style =
        window === null || rawOffset === undefined
          ? EMPTY_TEXT_INLINE_STYLE
          : styleAtIndex(window.map, rawOffset - window.offset);
      if (pieceStyle === null) {
        pieceStart = i;
        pieceStyle = style;
        continue;
      }
      if (isSameTextInlineStyle(style, pieceStyle)) continue;
      push(i);
      pieceStart = i;
      pieceStyle = style;
    }
    // 表示 0 文字のセグメント（括弧・URL だけに書式が付いたリンク等）は描画対象を持たないため
    // span を出さない（pieceStyle が null のまま残る）。
    push(segment.text.length);
  }
  return markCodeRangeEdges(spans);
}

/** 表示セグメントが覆う raw 範囲だけを持つ override 表（`offset` が raw offset の原点）。 */
interface SegmentOverrideWindow {
  readonly map: OverrideMap;
  readonly offset: number;
}

/**
 * 表示セグメントが実際に覆う raw 範囲（最小 offset 〜 最大 offset）だけの override 表を作る。
 * run が 1 件も無い場合と、表示文字が 1 つも無い場合は null（＝ override 無し）を返す。
 * 本文全体ぶんを確保しないため、1 行・1 セルあたりの確保量が本文長ではなくその範囲の長さになる。
 */
function createSegmentOverrideWindow(
  segments: readonly MarkdownDisplaySegment[],
  runs: readonly TextInlineStyleRange[] | undefined
): SegmentOverrideWindow | null {
  if ((runs?.length ?? 0) === 0) return null;
  let start = Number.POSITIVE_INFINITY;
  let end = Number.NEGATIVE_INFINITY;
  for (const segment of segments) {
    for (const rawOffset of segment.rawOffsets) {
      if (rawOffset < start) start = rawOffset;
      if (rawOffset + 1 > end) end = rawOffset + 1;
    }
  }
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
  const map = createOverrideMap(end - start);
  // 範囲と交差する run だけを、範囲の原点へ寄せて直接書き込む。run 配列の複製は作らない
  // （本関数は行・セルごとに呼ばれるため、交差しない run まで毎回複製するとセル数 × run 数になる）。
  // run の並び順は前提にしない（正規形でない配列を受け取る呼び出しもあるため）。
  for (const run of runs ?? []) {
    const from = Math.max(start, Math.floor(run.start));
    const to = Math.min(end, Math.floor(run.end));
    for (let index = from; index < to; index += 1) {
      const offset = index - start;
      if (run.style.color !== undefined) map.color[offset] = run.style.color;
      if (run.style.fill !== undefined) map.fill[offset] = run.style.fill;
      if (run.style.fontSize !== undefined) map.fontSize[offset] = run.style.fontSize;
      if (run.style.bold !== undefined) map.bold[offset] = run.style.bold;
    }
  }
  return { map, offset: start };
}

/**
 * 論理コード範囲の先頭・末尾の span へ `codeStart` / `codeEnd` を立てる。
 * コード範囲が部分書式の境界で複数の span に分かれても、padding と角丸は前後 1 回ずつだけに
 * なるようにする（DOM と Canvas が同じ根拠を使う）。
 */
function markCodeRangeEdges(
  spans: readonly TextInlineDisplaySpan[]
): readonly TextInlineDisplaySpan[] {
  const firstIndexOf = new Map<number, number>();
  const lastIndexOf = new Map<number, number>();
  for (const [index, span] of spans.entries()) {
    if (span.codeRangeIndex === undefined) continue;
    if (!firstIndexOf.has(span.codeRangeIndex)) firstIndexOf.set(span.codeRangeIndex, index);
    lastIndexOf.set(span.codeRangeIndex, index);
  }
  if (firstIndexOf.size === 0) return spans;
  return spans.map((span, index) => {
    if (span.codeRangeIndex === undefined) return span;
    return {
      ...span,
      ...(firstIndexOf.get(span.codeRangeIndex) === index ? { codeStart: true } : {}),
      ...(lastIndexOf.get(span.codeRangeIndex) === index ? { codeEnd: true } : {}),
    };
  });
}
